import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.graphics.drawscope.translate
import models.RoundModifier
import kotlin.math.sin

/** Quiet, non-interactive decoration along the board edges, keeping dice and controls clear. */
@Composable
internal fun SeasonalEventBackground(
    event: RoundModifier,
    modifier: Modifier = Modifier,
    animated: Boolean = true,
) {
    if (event != RoundModifier.FALL_HARVEST && event != RoundModifier.HALLOWEEN) return
    var seconds by remember(event) { mutableFloatStateOf(5f) }
    LaunchedEffect(event, animated) {
        if (!animated) return@LaunchedEffect
        var start = 0L
        var last = 0L
        while (true) withFrameNanos { now ->
            if (start == 0L) start = now
            if (now - last >= 33_000_000L) {
                seconds = ((now - start) % 120_000_000_000L) / 1_000_000_000f
                last = now
            }
        }
    }
    Canvas(modifier) {
        val autumn = event == RoundModifier.FALL_HARVEST
        val accent = if (autumn) Color(0xFFE49A39) else Color(0xFF9B65DC)
        val radius = size.minDimension * 0.38f
        for (corner in listOf(Offset.Zero, Offset(size.width, size.height))) {
            drawCircle(Brush.radialGradient(listOf(accent.copy(alpha = 0.1f), Color.Transparent),
                center = corner, radius = radius), radius, corner)
        }
        for (index in 0 until 14) {
            val side = if (index % 2 == 0) 0.035f else 0.965f
            val x = size.width * (side + sin(seconds * 0.6f + index * 2f) * 0.02f)
            val y = size.height * ((index / 14f + seconds / 120f) % 1f)
            val r = size.minDimension * (0.014f + index % 3 * 0.005f)
            translate(x, y) {
                rotate(sin(seconds + index) * 24f, pivot = Offset.Zero) {
                    if (autumn) {
                        val leaf = Path().apply {
                            moveTo(0f, -r * 1.4f)
                            quadraticTo(r * 1.7f, -r * 0.2f, 0f, r * 1.4f)
                            quadraticTo(-r * 1.7f, r * 0.2f, 0f, -r * 1.4f)
                            close()
                        }
                        val color = listOf(Color(0xFFEF9E3B), Color(0xFFC75439), Color(0xFFD5B85B))[index % 3]
                        drawPath(leaf, color.copy(alpha = 0.32f))
                        drawLine(color.copy(alpha = 0.4f), Offset(0f, -r), Offset(0f, r * 1.7f), 1f)
                    } else {
                        val wing = r * (0.8f + sin(seconds * 3f + index) * 0.2f)
                        val bat = Path().apply {
                            moveTo(0f, -r * 0.35f)
                            lineTo(-r * 0.2f, -r * 0.8f)
                            lineTo(-r * 0.4f, -r * 0.1f)
                            quadraticTo(-r, -wing, -r * 1.8f, -wing)
                            quadraticTo(-r * 1.5f, r * 0.7f, -r * 0.8f, r * 0.25f)
                            lineTo(0f, r * 0.8f)
                            lineTo(r * 0.8f, r * 0.25f)
                            quadraticTo(r * 1.5f, r * 0.7f, r * 1.8f, -wing)
                            quadraticTo(r, -wing, r * 0.4f, -r * 0.1f)
                            lineTo(r * 0.2f, -r * 0.8f)
                            close()
                        }
                        drawPath(bat, accent.copy(alpha = 0.3f))
                    }
                }
            }
        }
        if (!autumn) {
            // Little jack-o'-lanterns stay in the bottom corners instead of covering the pot.
            val r = size.minDimension * 0.032f
            for (x in listOf(r * 1.3f, size.width - r * 1.3f)) {
                val y = size.height - r * 1.4f
                drawOval(Color(0xFFFF8E32).copy(alpha = 0.34f), Offset(x - r, y - r), Size(r * 2, r * 1.7f))
                drawOval(Color(0xFFFFBE68).copy(alpha = 0.3f), Offset(x - r * 0.5f, y - r),
                    Size(r, r * 1.7f), style = Stroke(1f))
                drawLine(Color(0xFF89A85A).copy(alpha = 0.5f), Offset(x, y - r), Offset(x + r * 0.12f, y - r * 1.4f), r * 0.2f)
                drawCircle(Color(0xFF44214E).copy(alpha = 0.6f), r * 0.12f, Offset(x - r * 0.32f, y - r * 0.3f))
                drawCircle(Color(0xFF44214E).copy(alpha = 0.6f), r * 0.12f, Offset(x + r * 0.32f, y - r * 0.3f))
            }
        }
    }
}
